import React, { useState, useEffect } from 'react';
import { adsAPI, AdRecord } from '../utils/api';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const StartupAdModal: React.FC = () => {
    const { t } = useLanguage();
    const [ads, setAds] = useState<AdRecord[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const fetchAds = async () => {
            try {
                // Check if we've already shown an ad this session
                const lastShown = sessionStorage.getItem('last_startup_ad');
                if (lastShown) return;

                const activeAds = await adsAPI.getActive('startup');
                if (activeAds && activeAds.length > 0) {
                    // Take up to 5 ads
                    setAds(activeAds.slice(0, 5));
                    setIsVisible(true);
                    sessionStorage.setItem('last_startup_ad', Date.now().toString());
                }
            } catch (error) {
                console.error('Failed to fetch startup ads:', error);
            }
        };

        fetchAds();
    }, []);

    // Auto-rotate
    useEffect(() => {
        if (!isVisible || ads.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % ads.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isVisible, ads.length]);

    if (!isVisible || ads.length === 0) return null;

    const currentAd = ads[currentIndex];

    const nextAd = () => {
        setCurrentIndex((prev) => (prev + 1) % ads.length);
    };

    const prevAd = () => {
        setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <div className="relative bg-gray-900 rounded-2xl max-w-4xl w-full mx-4 overflow-hidden shadow-2xl border border-gray-700 flex flex-col md:flex-row group">

                {/* Close Button */}
                <button
                    onClick={() => setIsVisible(false)}
                    className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Left Arrow */}
                {ads.length > 1 && (
                    <button
                        onClick={prevAd}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/60 text-white rounded-full p-2 transition-all opacity-0 group-hover:opacity-100"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}

                {/* Image Section */}
                <div className="w-full md:w-2/3 h-64 md:h-auto relative">
                    <img
                        src={currentAd.imageUrl}
                        alt={currentAd.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent md:bg-gradient-to-r" />

                    {/* Dots Indicator */}
                    {ads.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                            {ads.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/80'
                                        }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="w-full md:w-1/3 p-8 flex flex-col justify-center bg-gray-900 relative">
                    <h2 className="text-3xl font-bold mb-4 text-white animate-in slide-in-from-right duration-300 key={currentIndex}">{currentAd.title}</h2>

                    {currentAd.description && (
                        <p className="text-gray-300 mb-6 text-sm line-clamp-4 animate-in slide-in-from-right duration-300 delay-75 key={currentIndex}">
                            {currentAd.description}
                        </p>
                    )}

                    {currentAd.linkUrl && (
                        <a
                            href={currentAd.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-blue-500/30 text-center"
                        >
                            {t('common.learn_more') || 'Learn More'}
                        </a>
                    )}

                    {/* Right Arrow */}
                    {ads.length > 1 && (
                        <button
                            onClick={nextAd}
                            className="absolute right-4 top-1/2 -translate-y-1/2 md:hidden z-20 bg-black/30 hover:bg-black/60 text-white rounded-full p-2 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}
                    {ads.length > 1 && (
                        <button
                            onClick={nextAd}
                            className="hidden md:block absolute -right-12 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors backdrop-blur-sm"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}

                </div>
            </div>
        </div>
    );
};

export default StartupAdModal;
